// JiraSyncHealthDot (US5 / PRODOPSAI-430)
//
// Passive 8px green/red indicator rendered next to the Status Count title on
// two surfaces:
//   - Metric View → Product Flow → Workflow Hygiene → `StatusCountCard`
//   - Management View → Quality Control → `MatrixCard` (Status Count only)
//
// Status semantics (locked by `IJiraSyncHealth`):
//   - `green` when every non-archived project's most recent sync succeeded.
//   - `red` when at least one non-archived project's sync failed. The dot is
//     intentionally informational — the count value still renders, no error
//     banner, no retry-fetch. Sync is out-of-band.
//   - There is NO amber state. Do not add one. (See US5 AC: "Frontend MUST
//     NOT fabricate an amber bucket.")
//
// `health === undefined` is the graceful-degradation path for responses that
// pre-date US4 (PRODOPSAI-429). Returns `null` — caller is unchanged.
//
// Tooltip copy is fixed by the US5 UX Notes section and verified by
// `__tests__/JiraSyncHealthDot.test.tsx`.

import type { IJiraSyncHealth } from '@skystone/shared';
import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

interface JiraSyncHealthDotProps {
  health: IJiraSyncHealth | undefined;
}

/**
 * Format an ISO-8601 timestamp using the user's locale. Returns `null` when
 * the input is `null` so callers can decide whether to omit the clause.
 */
function formatSyncedAt(syncedAt: string | null): string | null {
  if (syncedAt === null) return null;
  return new Date(syncedAt).toLocaleString();
}

/**
 * Compose the verbal tooltip / `aria-label` text for the three branches.
 *
 * Branches (matches US5 UX Notes verbatim):
 *   - green + syncedAt present → "Jira sync — All projects synced at HH:MM"
 *   - green + syncedAt null    → "Jira sync — No sync has run yet"
 *   - red                      → "Jira sync failed for N projects: KEY1,
 *                                  KEY2, … (synced HH:MM)"  (timestamp
 *                                  clause omitted if syncedAt is null)
 */
function buildTooltipText(health: IJiraSyncHealth): string {
  const formattedSyncedAt = formatSyncedAt(health.syncedAt);

  if (health.status === 'green') {
    if (formattedSyncedAt !== null) {
      return `Jira sync — All projects synced at ${formattedSyncedAt}`;
    }
    return 'Jira sync — No sync has run yet';
  }

  // red branch — backend ships `failedProjectKeys` already alphabetical.
  const projectCount = health.failedProjectKeys.length;
  const keyList = health.failedProjectKeys.join(', ');
  const base = `Jira sync failed for ${projectCount} projects: ${keyList}`;
  if (formattedSyncedAt !== null) {
    return `${base} (synced ${formattedSyncedAt})`;
  }
  return base;
}

export function JiraSyncHealthDot({ health }: Readonly<JiraSyncHealthDotProps>) {
  if (health === undefined) return null;

  const tooltipText = buildTooltipText(health);
  const dotColorClass = health.status === 'green' ? 'bg-success' : 'bg-error';

  return (
    <Tooltip content={tooltipText} side="top">
      <span
        data-testid="jira-sync-health-dot"
        data-status={health.status}
        role="img"
        aria-label={tooltipText}
        className={cn('inline-block h-2 w-2 rounded-full', dotColorClass)}
      />
    </Tooltip>
  );
}

export default JiraSyncHealthDot;
